import { kstShiftDay, kstToday } from '@shared/utils/kstTime.util';

/**
 * 통계 기간 단위. 월 → 반기까지 한 번에 고르고 ◀▶로 앞뒤 기간을 넘긴다.
 * 계산은 전부 'YYYY-MM'(달) 문자열 산술이라 시간대 영향을 받지 않는다.
 */
export type PeriodUnit = 'month' | 'bimonth' | 'quarter' | 'half' | 'custom';

export const PERIOD_UNIT_LABELS: Record<PeriodUnit, string> = {
  month: '월',
  bimonth: '2개월',
  quarter: '분기',
  half: '반기',
  custom: '직접',
};

/** 단위가 걸치는 달 수 */
export const monthSpanOf = (unit: PeriodUnit): number => {
  switch (unit) {
    case 'bimonth':
      return 2;
    case 'quarter':
      return 3;
    case 'half':
      return 6;
    default:
      return 1;
  }
};

export interface DateRange {
  from: string;
  to: string;
}

/** 'YYYY-MM'에 delta 달을 더한다 */
export const addMonths = (ym: string, delta: number): string => {
  const [year, month] = ym.split('-').map(Number);
  const total = (year ?? 0) * 12 + ((month ?? 1) - 1) + delta;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
};

/** 'YYYY-MM'의 말일 'YYYY-MM-DD' — 다음 달 1일에서 하루 뺀다 */
export const lastDayOf = (ym: string): string => kstShiftDay(`${addMonths(ym, 1)}-01`, -1);

/** KST 기준 이번 달 'YYYY-MM' */
export const currentYm = (): string => kstToday().slice(0, 7);

/**
 * 단위에 맞게 앵커 달을 기간 시작 달로 정렬한다.
 * 분기는 1·4·7·10월, 반기는 1·7월에서 시작해야 "3분기"·"하반기"가 말이 된다.
 * 2개월은 정해진 경계가 없어 앵커를 그대로 시작 달로 쓴다.
 */
export const alignAnchor = (ym: string, unit: PeriodUnit): string => {
  const [year, month] = ym.split('-').map(Number);
  const m = month ?? 1;
  if (unit === 'quarter') {
    const start = Math.floor((m - 1) / 3) * 3 + 1;
    return `${year}-${String(start).padStart(2, '0')}`;
  }
  if (unit === 'half') {
    return `${year}-${m <= 6 ? '01' : '07'}`;
  }
  return ym;
};

/**
 * 앵커 달 + 단위 → 조회 범위.
 * 오늘이 기간 안에 들어 있으면 끝을 오늘로 자른다 — 아직 오지 않은 날까지 0으로
 * 그리면 추이 차트가 뚝 떨어진 것처럼 보이기 때문.
 */
export const rangeOfPeriod = (ym: string, unit: PeriodUnit): DateRange => {
  const start = alignAnchor(ym, unit);
  const end = lastDayOf(addMonths(start, monthSpanOf(unit) - 1));
  const today = kstToday();
  return { from: `${start}-01`, to: end > today ? today : end };
};

/** 기간 표기 — 화면 가운데에 크게 뜨는 라벨 */
export const periodLabel = (ym: string, unit: PeriodUnit): string => {
  const start = alignAnchor(ym, unit);
  const [year, month] = start.split('-').map(Number);
  const m = month ?? 1;

  switch (unit) {
    case 'month':
      return `${year}년 ${m}월`;
    case 'bimonth': {
      const endYm = addMonths(start, 1);
      const [endYear, endMonth] = endYm.split('-').map(Number);
      //* 해를 넘기면 뒤쪽 연도도 같이 보여야 헷갈리지 않는다
      return endYear === year
        ? `${year}년 ${m}–${endMonth}월`
        : `${year}년 ${m}월–${endYear}년 ${endMonth}월`;
    }
    case 'quarter':
      return `${year}년 ${Math.floor((m - 1) / 3) + 1}분기 (${m}–${m + 2}월)`;
    case 'half':
      return m <= 6 ? `${year}년 상반기 (1–6월)` : `${year}년 하반기 (7–12월)`;
    default:
      return `${year}년 ${m}월`;
  }
};

/** 기간 일수 (양끝 포함) */
export const daysBetween = (from: string, to: string): number =>
  Math.floor(
    (new Date(`${to}T00:00:00+09:00`).getTime() -
      new Date(`${from}T00:00:00+09:00`).getTime()) /
      86_400_000,
  ) + 1;

/** 앵커가 현재 기간인지 — "현재" 버튼을 보일지 정한다 */
export const isCurrentPeriod = (ym: string, unit: PeriodUnit): boolean =>
  alignAnchor(ym, unit) === alignAnchor(currentYm(), unit);
